import { type FunctionComponent, useEffect, useState } from 'react';
import { Form } from 'antd';
import type { FormInstance } from 'antd/lib/form';
import { sortBy } from 'lodash';
import axios from 'axios';
import moment from 'moment';
import { useUploadContext } from 'components/FileUploader';
import { Spinner, useMessage } from 'components/ui';
import {
  useGetDamageSchemasForIngestionQuery,
  useCreateSignedUploadMutation,
  useCompleteUploadMutation,
  type AtlasGqlDamageSchema,
} from 'types/atlas-graphql';
import { UploadForm } from './UploadForm';
import { Confirmation } from './Confirmation';
import { ErrorState } from './ErrorState';
import { STEPS } from './common';
import {
  type DataField,
  type PreviewDamage,
  type HeaderMappingEntry,
  type SchemaOptions,
  type ImportResult,
} from '../types';

type ModalContentProps = {
  inspectionId?: string;
  multiInspection?: boolean;
  setModalOpen: (open: boolean) => void;
  step: number;
  setStep: (step: number) => void;
  form: FormInstance;
  handleCheckStatus: (bundleId: string) => void;
  onReset: () => void;
  importResult: ImportResult | undefined;
  ErrorStateComponent?: FunctionComponent<{
    importResult: ImportResult | undefined;
    importedFileName: string;
  }>;
};

const INSPECTION_DAMAGE_DATA_TYPE = 'InspectionDamage';

const commonDataFields: DataField[] = [
  {
    key: 'IMAGE_NAME',
    label: 'Photo File Name',
    required: true,
  },
  {
    key: 'COORDINATES',
    label: 'Coordinates',
    required: true,
  },
];

const assetLookupFields: DataField[] = [
  {
    key: 'INSPECTION_DATE',
    label: 'Inspection Date',
    required: true,
  },
  {
    key: 'SITE_NAME',
    label: 'Site',
    required: true,
  },
  {
    key: 'TURBINE_NAME',
    label: 'Turbine',
    required: true,
  },
  {
    key: 'COMPONENT_UNIQUE_NAME',
    label: 'Component Name',
    required: false,
  },
];

export const ModalContent: React.FunctionComponent<ModalContentProps> = ({
  inspectionId,
  setModalOpen,
  step,
  setStep,
  form,
  handleCheckStatus,
  onReset,
  importResult,
  multiInspection,
  ErrorStateComponent,
}) => {
  const handleResetAndClose = () => {
    setStep(STEPS.upload);
    setModalOpen(false);
    onReset();
  };
  const targetInspectionId = !!inspectionId && !multiInspection ? inspectionId : undefined;

  const message = useMessage();
  const [headerMapping, setHeaderMapping] = useState<HeaderMappingEntry[]>([]);

  const fieldsForInspectionLookup = !targetInspectionId ? assetLookupFields : [];
  const commonFields = [...commonDataFields, ...fieldsForInspectionLookup];

  const [dataFields, setDataFields] = useState<DataField[]>(commonFields);
  const [previewDamage, setPreviewDamage] = useState<PreviewDamage>();

  const { selectedFiles, setSelectedFiles, setUploading } = useUploadContext();

  const { data: damageSchemaData, loading } = useGetDamageSchemasForIngestionQuery();

  const schemaOptions: SchemaOptions[] =
    damageSchemaData?.damageSchemas.map(({ id, name }) => ({ value: id, label: name ?? id })) ?? [];

  const defaultSchema = damageSchemaData?.damageSchemas?.find(({ isDefault }) => isDefault);

  const setFieldsForSchema = (schema: AtlasGqlDamageSchema) => {
    const fieldsForSchema: DataField[] = sortBy(schema?.definitions ?? [], 'sortIndex').map(
      ({ id, name }) => ({ key: id, label: name })
    );

    setDataFields([...commonFields, ...fieldsForSchema]);

    // TODO: attempt to map fields automatically
  };

  useEffect(() => {
    setFieldsForSchema(defaultSchema as AtlasGqlDamageSchema);
  }, [defaultSchema]);

  const handleClickPreview = async () => {
    try {
      await form.validateFields();
    } catch (errorInfo) {
      if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
        form.scrollToField(errorInfo.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    const values = form.getFieldsValue(true);
    setHeaderMapping(
      Object.entries(values)
        .filter(
          ([mappedKey, value]: [string, string]) =>
            value && dataFields?.find(({ key }) => key === mappedKey)
        )
        .map(([key, value]: [string, string]) => {
          return { fileHeader: value, schemaFieldId: key };
        })
    );

    setStep(STEPS.confirmation);
  };

  const handleFileUpload = async (url: string): Promise<void> => {
    const { file } = form.getFieldsValue(['file']);
    const buf = await file.file.arrayBuffer();

    await axios.put(url, buf);
  };

  const [createSignedUpload] = useCreateSignedUploadMutation();

  const [completeUpload] = useCompleteUploadMutation();

  const handleSubmit = async () => {
    const values = form.getFieldsValue(['damageSchemaId', 'file']);
    const fileName: string = values.file.file.name;

    setUploading(true);

    setSelectedFiles([
      { name: fileName, path: '', percent: 0, sourceName: fileName, type: 'file', valid: true },
    ]);

    const bundleId = moment().format('x');

    const input = {
      bundleId,
      recordId: targetInspectionId ? targetInspectionId : bundleId,
      dataType: INSPECTION_DAMAGE_DATA_TYPE,
      files: [{ fileName }],
      params: {
        inspectionId: targetInspectionId,
        multiInspection: !targetInspectionId,
        headerMapping,
        damageSchemaId: values.damageSchemaId,
      },
    };

    const { data: createSignedUploadData } = await createSignedUpload({ variables: { input } });

    const { url } = createSignedUploadData?.createSignedUpload?.[0] ?? {};

    if (url) {
      try {
        await handleFileUpload(url);
        await completeUpload({ variables: { input } });
        setUploading(false);
      } catch (error) {
        console.error(error);
        message.error('Error uploading. Please try again.');
      }

      handleCheckStatus(bundleId);
      onReset();
    }

    handleResetAndClose();
  };

  const handleFieldChange = (changedFields: { name: string[]; value: any }[]) => {
    const damageSchemaIdField = changedFields.find(({ name }) => name.includes('damageSchemaId'));

    if (damageSchemaIdField) {
      const currentSchema = damageSchemaData?.damageSchemas?.find(
        ({ id }) => id === damageSchemaIdField.value
      );

      setFieldsForSchema(currentSchema as AtlasGqlDamageSchema);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFieldsChange={handleFieldChange}
      initialValues={{ damageSchemaId: defaultSchema?.id }}
    >
      <UploadForm
        schemaOptions={schemaOptions}
        dataFields={dataFields}
        setPreviewDamage={setPreviewDamage}
        step={step}
        setStep={setStep}
        onClose={handleResetAndClose}
        onClickPreview={handleClickPreview}
        getFieldsValue={form.getFieldsValue}
        resetFields={(fields?: string[]) => form.resetFields(fields)}
        fieldsHidden={step >= STEPS.confirmation}
      />

      {step === STEPS.confirmation && previewDamage && (
        <Confirmation
          dataFields={dataFields}
          previewDamage={previewDamage}
          headerMapping={headerMapping}
          setStep={setStep}
          handleSubmit={handleSubmit}
        />
      )}
      {step === STEPS.error &&
        (ErrorStateComponent ? (
          <ErrorStateComponent
            importResult={importResult}
            importedFileName={selectedFiles?.[0]?.name ?? 'your file'}
          />
        ) : (
          <ErrorState
            importResult={importResult}
            importedFileName={selectedFiles?.[0]?.name ?? 'your file'}
          />
        ))}
    </Form>
  );
};
